/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

/* eslint-disable */
//remove the above when we have a proper library solution

import dayjs from 'dayjs';
import { default as inBetween } from 'dayjs/plugin/isBetween';
import { default as isSameOrAfter } from 'dayjs/plugin/isSameOrAfter';
import { LinkMap } from './link-map';
import { PLAN_CONSTANTS } from './plan-constants';

dayjs.extend(inBetween);
dayjs.extend(isSameOrAfter);

/** wraps a couple very specific date operations that all called frequenent in a cache */
export class DateCache {
	public static defaultFormat = 'YYYY-MM-DD';
	/** dayjs of today */
	public today!: dayjs.Dayjs;
	/** string date of today */
	public todayString!: string;

	private generalCache = new Map<string, dayjs.Dayjs>();
	private addDaysCache = new LinkMap<string, string, number>();
	private substractDaysCache = new LinkMap<string, string, number>();
	private dateDiffCache = new LinkMap<number>();
	private dowCache = new Map<string, number>();
	private formatCache: any = {}; // moved from datejar, maybe we can use a Map

	private _timeoutRef!: ReturnType<typeof setTimeout>;

	constructor() {
		this.updateToday();
		this.setupCacheClearing();
	}

	/** cached dayjs(date); */
	protected getDayjs(date: string): dayjs.Dayjs {
		const thing = this.generalCache.get(date);
		if (thing) {
			return thing;
		}
		const dobj = dayjs(date);
		this.generalCache.set(date, dobj);

		return dobj;
	}

	/** cached dayjs(startDate).add(days, 'days'); */
	public addDays(startDate: string, days: number) {
		let thing = this.addDaysCache.get(startDate, days);
		if (thing) {
			return thing;
		}
		const adjusted = dayjs(startDate).add(days, 'day');
		thing = this.getFormat(adjusted);
		this.addDaysCache.set(startDate, days, thing);

		return thing;
	}

	/** cached dayjs(startDate).subtract(days, 'days'); */
	public substractDays(startDate: string, days: number) {
		let thing = this.substractDaysCache.get(startDate, days);
		if (thing) {
			return thing;
		}
		const adjusted = dayjs(startDate).subtract(days, 'day');
		thing = this.getFormat(adjusted);
		this.substractDaysCache.set(startDate, days, thing);

		return thing;
	}

	/** cached dayjs(startDate).diff(endDate, 'days'); */
	public diffDays(startDate: string, endDate: string) {
		let thing = this.dateDiffCache.get(startDate, endDate);
		if (thing) {
			return thing;
		}
		// console.log('diff', startDate, endDate);
		thing = dayjs(startDate).diff(endDate, 'day');
		this.dateDiffCache.set(startDate, endDate, thing);

		return thing;
	}

	/** cached dayjs(date).day(); */
	public getDow(date: string) {
		let thing = this.dowCache.get(date);
		if (thing) {
			return thing;
		}
		thing = dayjs(date).day();
		this.dowCache.set(date, thing);

		return thing;
	}

	/** cached dayjs(date).format(format); */
	public getFormat(date: any, format: string = DateCache.defaultFormat): string {
		if (!date) {
			return '';
		}

		if (this.formatCache[date] && this.formatCache[date][format]) {
			return this.formatCache[date][format];
		}
		const fDate = dayjs(date).format(format);
		if (!this.formatCache[date]) {
			this.formatCache[date] = {};
		}
		this.formatCache[date][format] = fDate;

		return fDate;
	}

	/** The following are needed to be compatible with current usages in existing code using moment version of this class
	 * For isBetween, isBefore, and isSameAndAfter, the are currently called from a single place. if later we actually
	 * need to cache the results of them, cache could be added here.
	 */
	public getFormatByDate(date: Date): string {
		return this.getFormat(dayjs(date).format(DateCache.defaultFormat));
	}
	public isBetween(date: string, start: string, end: string, inclusive: '()' | '[]' | '[)' | '(]'): boolean {
		return this.getDayjs(date).isBetween(this.getDayjs(start), this.getDayjs(end), null, inclusive);
	}
	public isBefore(date: Date, value: string): boolean {
		return this.getDayjs(this.getFormatByDate(date)).isBefore(value);
	}
	public isSameOrAfter(date: Date, value: string): boolean {
		return this.getDayjs(this.getFormatByDate(date)).isSameOrAfter(value);
	}
	public getDate(date: string): Date {
		return this.getDayjs(date).toDate();
	}
	public getMonday(date: string): string {
		const d = this.getDate(date);
		const dow = d.getDay();
		const diff = d.getDate() - dow + (dow === 0 ? -6 : 1); // if Sunday, go back 6 days
		d.setDate(diff);
		return this.getFormat(d);
	}

	//set a timeout to reset this once midnight hits
	private updateToday() {
		this.today = dayjs();
		this.todayString = this.getFormat(this.today, DateCache.defaultFormat); //this.today.format(DateCache.defaultFormat);
	}

	private setupCacheClearing() {
		const now = new Date();
		const night = new Date(
			now.getFullYear(),
			now.getMonth(),
			now.getDate() + 1, // the next day, ...
			0,
			0,
			0 // ...at 00:00:00 hours
		);
		const msToMidnight = night.getTime() - now.getTime();
		this._timeoutRef = setTimeout(() => {
			this.clearCache();
			this.setupCacheClearing();
		}, msToMidnight);
	}

	public clearCache() {
		this.updateToday();
		this.addDaysCache = new LinkMap();
		this.substractDaysCache = new LinkMap();
		this.dateDiffCache = new LinkMap();
		this.dowCache = new Map();
		this.formatCache = {};
		clearTimeout(this._timeoutRef);
	}

	/**
	 * this is only useful for long running contexts, and actively harmful to unit tests
	 * can call to revoke the timer anytime after initialization
	 */
	public disableTimer() {
		clearTimeout(this._timeoutRef);
	}

	/**
	 * Formats a range of dates into a readable ticket-style string.
	 *
	 * Scenarios:
	 * - Same day: "Jan 1" or "Jan 1 2025"
	 * - Same month and year: "Jan 1 - 22" or "Jan 1 - 22 2025"
	 * - Different months, same year: "Jan 31 - Feb 2" or "Jan 31 - Feb 2 2025"
	 * - Different years: "Dec 31 - Jan 1" or "Dec 31 2024 - Jan 1 2025"
	 *
	 * @param startDate - The start date in ISO format (e.g. "2025-01-01")
	 * @param endDate - The end date in ISO format (e.g. "2025-01-22")
	 * @param includeYear - Optional. Whether to include the year in the output. Defaults to false.
	 * @returns A formatted string representing the date or date range
	 */
	ticketDateStringConvert(startDate: string, endDate: string, includeYear: boolean = false): string {
		if (!startDate || !endDate) {
			return '';
		}
		const startFormatted = this.getFormat(startDate, 'MMM D YYYY');
		const endFormatted = this.getFormat(endDate, 'MMM D YYYY');

		const [startMonth, startDay, startYear] = startFormatted.split(' ');
		const [endMonth, endDay, endYear] = endFormatted.split(' ');

		const sameDay = startDay === endDay && startMonth === endMonth && startYear === endYear;
		const sameMonth = startMonth === endMonth && startYear === endYear;
		const sameYear = startYear === endYear;

		if (sameDay) {
			// Example: Jan 1 2025 or Jan 1
			return includeYear ? `${startMonth} ${startDay} ${startYear}` : `${startMonth} ${startDay}`;
		}

		if (sameYear) {
			if (sameMonth) {
				// Example: Jan 1 - 22 2025 or Jan 1 - 22
				return includeYear
					? `${startMonth} ${startDay} - ${endDay} ${startYear}`
					: `${startMonth} ${startDay} - ${endDay}`;
			} else {
				// Example: Jan 31 - Feb 2 2025 or Jan 31 - Feb 2
				return includeYear
					? `${startMonth} ${startDay} - ${endMonth} ${endDay} ${startYear}`
					: `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
			}
		}

		// Different years — Example: Dec 31 2024 - Jan 1 2025 or Dec 31 - Jan 1
		return includeYear
			? `${startMonth} ${startDay} ${startYear} - ${endMonth} ${endDay} ${endYear}`
			: `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
	}
}

export class DangerousDateCache extends DateCache {
	/**
	 * DO NOT MUTATE or use at all wherever possible
	 * This exists for the rare situtation where we need the dayjs object for querying
	 * but cloning it defeats the purpose of the cache
	 */
	public getCachedDayjs(date: string): dayjs.Dayjs {
		return this.getDayjs(date);
	}
}

export const dateCache = new DateCache();
